/*
 * Полный экран звонка на телефоне — запись в истории браузера с меткой
 * { codeCall: 1 }: системная «назад» тогда сворачивает звонок, а не
 * уводит с экрана приложения. Ключ отдельный от стопки экранов
 * ({ codeNav }), чтобы стопка эту запись не считала своей.
 *
 * История передаётся параметром со значением по умолчанию window.history —
 * так же, как в nav/controller.ts — чтобы в тестах подставлять подделку.
 */

export type CallHistoryLike = {
  readonly state: unknown;
  pushState(state: unknown, title: string): void;
  back(): void;
};

const MARK = 'codeCall';

const isCallState = (s: unknown): boolean =>
  !!s && typeof s === 'object' && (s as Record<string, unknown>)[MARK] === 1;

export function hasCallEntry(history: CallHistoryLike = window.history): boolean {
  return isCallState(history.state);
}

export function pushCallEntry(history: CallHistoryLike = window.history): void {
  if (hasCallEntry(history)) return;
  history.pushState({ [MARK]: 1 }, '');
}

/** Снять свою запись, если она наверху; popstate при этом придёт, но звонок уже не полный. */
export function popCallEntry(history: CallHistoryLike = window.history): void {
  if (hasCallEntry(history)) history.back();
}

/**
 * Обработчик popstate: зовёт onPop, как только запись звонка не наверху
 * истории (человек нажал «назад»). Флаг «была ли запись раньше» не нужен —
 * onPop у провайдера сам не трогает состояния idle/ended/mini.
 */
export function callPopHandler(onPop: () => void, history: CallHistoryLike = window.history): () => void {
  return () => {
    if (!hasCallEntry(history)) onPop();
  };
}

/** Зовёт onPop, когда запись звонка ушла из истории (человек нажал «назад»). */
export function watchCallPop(onPop: () => void, history: CallHistoryLike = window.history): () => void {
  const handler = callPopHandler(onPop, history);
  window.addEventListener('popstate', handler);
  return () => window.removeEventListener('popstate', handler);
}
